import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  InputAdornment,
  Slider,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

interface HeaderSideButtonSettingsProps {
  config: any;
  onHeaderSettingChange: (setting: string, value: any) => void;
}

const colorField = (
  label: string,
  value: string,
  fallback: string,
  disabled: boolean,
  onChange: (next: string) => void
) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value || fallback}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Box
            component="input"
            type="color"
            value={value || fallback}
            disabled={disabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
          />
        </InputAdornment>
      ),
    }}
  />
);

/**
 * Header sağındaki Kurumsal Giriş ve Başvuru butonları.
 * Pasifken vitrinde görünmez; kurumsal giriş açıkken kullanıcı ikonu gizlenir.
 */
const HeaderSideButtonSettings: React.FC<HeaderSideButtonSettingsProps> = ({
  config,
  onHeaderSettingChange,
}) => {
  const { t } = useTranslation('salesSystem');
  const hs = config.headerSettings || {};
  const corporateOn = hs.showCorporateLoginButton === true;
  const applicationOn = hs.showApplicationButton === true;
  const corporateText = hs.corporateLoginText || 'Kurumsal Giriş';
  const corporateBg = hs.corporateLoginBackgroundColor || '#0B1B3F';
  const corporateFg = hs.corporateLoginTextColor || '#ffffff';
  const corporateSize = Number(hs.corporateLoginFontSize || 14);
  const applicationText = hs.applicationButtonText || 'Başvuru';
  const applicationBg = hs.applicationButtonBackgroundColor || '#E10613';
  const applicationFg = hs.applicationButtonTextColor || '#ffffff';
  const applicationSize = Number(hs.applicationButtonFontSize || 14);

  const previewStyle = (bg: string, fg: string, size: number): React.CSSProperties => ({
    backgroundColor: bg,
    color: fg,
    fontSize: size,
    fontWeight: 600,
    borderRadius: 999,
    padding: `${Math.max(6, Math.round(size * 0.42))}px ${Math.max(14, Math.round(size * 1.15))}px`,
    lineHeight: 1.2,
  });

  return (
    <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {t('ecommerceHeaderSettings.sideButtons.title')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('ecommerceHeaderSettings.sideButtons.help')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2, minHeight: 40 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            {t('ecommerceHeaderSettings.sideButtons.preview')}
          </Typography>
          {corporateOn && <Box style={previewStyle(corporateBg, corporateFg, corporateSize)}>{corporateText}</Box>}
          {applicationOn && (
            <Box style={previewStyle(applicationBg, applicationFg, applicationSize)}>{applicationText}</Box>
          )}
          {!corporateOn && !applicationOn && (
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceHeaderSettings.sideButtons.previewEmpty')}
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('ecommerceHeaderSettings.sideButtons.corporateTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('ecommerceHeaderSettings.sideButtons.corporateHelp')}
        </Typography>
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={corporateOn}
              onChange={(e) => onHeaderSettingChange('showCorporateLoginButton', e.target.checked)}
            />
          }
          label={t('ecommerceHeaderSettings.common.active')}
        />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.sideButtons.text')}
              value={corporateText}
              disabled={!corporateOn}
              onChange={(e) => onHeaderSettingChange('corporateLoginText', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.sideButtons.url')}
              value={hs.corporateLoginUrl || '/account'}
              disabled={!corporateOn}
              helperText={t('ecommerceHeaderSettings.sideButtons.urlHelpCorporate')}
              onChange={(e) => onHeaderSettingChange('corporateLoginUrl', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {colorField(
              t('ecommerceHeaderSettings.sideButtons.background'),
              corporateBg,
              '#0B1B3F',
              !corporateOn,
              (next) => onHeaderSettingChange('corporateLoginBackgroundColor', next)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {colorField(
              t('ecommerceHeaderSettings.sideButtons.textColor'),
              corporateFg,
              '#ffffff',
              !corporateOn,
              (next) => onHeaderSettingChange('corporateLoginTextColor', next)
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              {t('ecommerceHeaderSettings.sideButtons.size')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
              <Slider
                value={corporateSize}
                disabled={!corporateOn}
                min={12}
                max={22}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, value) => onHeaderSettingChange('corporateLoginFontSize', value)}
              />
              <Typography variant="body2" sx={{ minWidth: 48 }}>
                {corporateSize}px
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('ecommerceHeaderSettings.sideButtons.applicationTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('ecommerceHeaderSettings.sideButtons.applicationHelp')}
        </Typography>
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              checked={applicationOn}
              onChange={(e) => onHeaderSettingChange('showApplicationButton', e.target.checked)}
            />
          }
          label={t('ecommerceHeaderSettings.common.active')}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.sideButtons.text')}
              value={applicationText}
              disabled={!applicationOn}
              onChange={(e) => onHeaderSettingChange('applicationButtonText', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.sideButtons.url')}
              value={hs.applicationButtonUrl || '/basvuru'}
              disabled={!applicationOn}
              helperText={t('ecommerceHeaderSettings.sideButtons.urlHelpApplication')}
              onChange={(e) => onHeaderSettingChange('applicationButtonUrl', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {colorField(
              t('ecommerceHeaderSettings.sideButtons.background'),
              applicationBg,
              '#E10613',
              !applicationOn,
              (next) => onHeaderSettingChange('applicationButtonBackgroundColor', next)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {colorField(
              t('ecommerceHeaderSettings.sideButtons.textColor'),
              applicationFg,
              '#ffffff',
              !applicationOn,
              (next) => onHeaderSettingChange('applicationButtonTextColor', next)
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              {t('ecommerceHeaderSettings.sideButtons.size')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
              <Slider
                value={applicationSize}
                disabled={!applicationOn}
                min={12}
                max={22}
                step={1}
                valueLabelDisplay="auto"
                onChange={(_, value) => onHeaderSettingChange('applicationButtonFontSize', value)}
              />
              <Typography variant="body2" sx={{ minWidth: 48 }}>
                {applicationSize}px
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default HeaderSideButtonSettings;
